<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :closable="true"
    :draggable="false"
    class="image-viewer-dialog"
    :header="title"
    :style="dialogStyle"
    :content-style="dialogContentStyle"
    @hide="handleClose"
  >
    <div class="image-viewer-container" @wheel.prevent="handleWheel">
      <div
        ref="imageWrapper"
        class="image-wrapper"
        :style="isVideo ? {} : imageWrapperStyle"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseUp"
      >
        <!-- 视频播放器 -->
        <video
          v-if="isVideo"
          :src="imageUrl"
          class="viewer-image"
          controls
          autoplay
          muted
          playsinline
        />
        <!-- 图片显示 -->
        <img
          v-else
          ref="imageElement"
          :src="imageUrl"
          :alt="title"
          class="viewer-image"
          draggable="false"
          @load="handleImageLoad"
        />
      </div>

      <!-- 工具栏 (只在图片模式下显示) -->
      <div v-if="!isVideo" class="viewer-toolbar">
        <Button
          icon="pi pi-minus"
          text
          rounded
          severity="secondary"
          @click="zoomOut"
        />
        <span class="zoom-level">{{ Math.round(scale * 100) }}%</span>
        <Button
          icon="pi pi-plus"
          text
          rounded
          severity="secondary"
          @click="zoomIn"
        />
        <Button
          icon="pi pi-refresh"
          text
          rounded
          severity="secondary"
          @click="resetView"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { computed, onUnmounted, ref, watch } from 'vue'

import { detectMediaType } from '@/utils/mediaUtils'

interface ImageViewerDialogProps {
  visible?: boolean
  imageUrl?: string
  title?: string
}

interface ImageViewerDialogEmits {
  (e: 'update:visible', value: boolean): void
}

const props = withDefaults(defineProps<ImageViewerDialogProps>(), {
  visible: false,
  imageUrl: '',
  title: '图片预览'
})

const emit = defineEmits<ImageViewerDialogEmits>()

// 响应式数据
const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

const scale = ref(1)
const initialScale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
const isDragging = ref(false)
const dragStartX = ref(0)
const dragStartY = ref(0)
const dragStartTranslateX = ref(0)
const dragStartTranslateY = ref(0)

const imageWrapper = ref<HTMLElement | null>(null)
const imageElement = ref<HTMLImageElement | null>(null)

const dialogStyle = {
  width: '100vw',
  height: '100vh',
  maxHeight: '100vh'
}

const dialogContentStyle = {
  padding: '0px',
  display: 'flex',
  flexDirection: 'column' as const,
  height: 'calc(100vh - 56px)',
  maxHeight: 'calc(100vh - 56px)'
}

// 检测是否为视频
const isVideo = computed(() => {
  return detectMediaType(props.imageUrl) === 'video'
})

// 计算样式
const imageWrapperStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
  cursor: isDragging.value ? 'grabbing' : 'grab'
}))

// 滚轮缩放
const handleWheel = (event: WheelEvent) => {
  if (isVideo.value) return
  event.preventDefault()
  const delta = event.deltaY > 0 ? -0.1 : 0.1
  const newScale = Math.max(0.1, Math.min(5, scale.value + delta))
  scale.value = newScale
}

// 缩放按钮
const zoomIn = () => {
  scale.value = Math.min(5, scale.value + 0.2)
}

const zoomOut = () => {
  scale.value = Math.max(0.1, scale.value - 0.2)
}

// 重置视图到当前图片的初始缩放
const resetView = () => {
  scale.value = initialScale.value
  translateX.value = 0
  translateY.value = 0
}

// 拖动处理
const handleMouseDown = (event: MouseEvent) => {
  if (isVideo.value) return
  event.preventDefault()
  event.stopPropagation()
  isDragging.value = true
  dragStartX.value = event.clientX
  dragStartY.value = event.clientY
  dragStartTranslateX.value = translateX.value
  dragStartTranslateY.value = translateY.value
  // 添加全局事件监听
  document.addEventListener('mousemove', handleGlobalMouseMove)
  document.addEventListener('mouseup', handleGlobalMouseUp)
}

const handleMouseMove = (event: MouseEvent) => {
  if (isVideo.value) return
  if (!isDragging.value) return
  event.preventDefault()
  event.stopPropagation()

  const deltaX = event.clientX - dragStartX.value
  const deltaY = event.clientY - dragStartY.value

  translateX.value = dragStartTranslateX.value + deltaX
  translateY.value = dragStartTranslateY.value + deltaY
}

// 全局鼠标移动处理（用于拖动时鼠标移出容器的情况）
const handleGlobalMouseMove = (event: MouseEvent) => {
  if (!isDragging.value) return
  event.preventDefault()
  event.stopPropagation()

  const deltaX = event.clientX - dragStartX.value
  const deltaY = event.clientY - dragStartY.value

  translateX.value = dragStartTranslateX.value + deltaX
  translateY.value = dragStartTranslateY.value + deltaY
}

const handleMouseUp = () => {
  if (isVideo.value) return
  isDragging.value = false
  // 移除全局事件监听
  document.removeEventListener('mousemove', handleGlobalMouseMove)
  document.removeEventListener('mouseup', handleGlobalMouseUp)
}

// 全局鼠标抬起处理
const handleGlobalMouseUp = () => {
  isDragging.value = false
  // 移除全局事件监听
  document.removeEventListener('mousemove', handleGlobalMouseMove)
  document.removeEventListener('mouseup', handleGlobalMouseUp)
}

// 根据图片比例和容器大小计算合适的初始缩放
const calculateInitialScale = () => {
  const img = imageElement.value
  const wrapper = imageWrapper.value
  if (!img || !wrapper) return

  const naturalWidth = img.naturalWidth
  const naturalHeight = img.naturalHeight
  if (!naturalWidth || !naturalHeight) return

  const containerRect = wrapper.parentElement?.getBoundingClientRect()
  if (!containerRect) return

  const containerWidth = containerRect.width
  const containerHeight = containerRect.height

  // 以不裁剪为前提，计算“刚好放下”的比例
  const fitScale = Math.min(
    containerWidth / naturalWidth,
    containerHeight / naturalHeight
  )

  // 大图无需额外缩放，由 CSS contain 适配；小图允许放大至设定倍数
  if (fitScale <= 1) {
    initialScale.value = 1
    return
  }

  const MAX_SCALE = 3
  const baseScale = Math.min(MAX_SCALE, fitScale * 0.9)
  initialScale.value = baseScale
}

// 图片加载完成
const handleImageLoad = () => {
  calculateInitialScale()
  resetView()
}

// 关闭对话框
const handleClose = () => {
  resetView()
}

// 监听visible变化，重置视图
watch(
  () => props.visible,
  (newValue) => {
    if (newValue) {
      resetView()
    } else {
      // 关闭时清理拖动状态
      isDragging.value = false
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }
)

// 组件卸载时清理事件监听
onUnmounted(() => {
  document.removeEventListener('mousemove', handleGlobalMouseMove)
  document.removeEventListener('mouseup', handleGlobalMouseUp)
})
</script>

<style scoped>
.image-viewer-dialog :deep(.p-dialog) {
  background: #000;
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.image-viewer-dialog :deep(.p-dialog-header) {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.image-viewer-dialog :deep(.p-dialog-content) {
  background: #000;
  padding: 0;
  overflow: hidden;
  flex: 1;
}

.image-viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-wrapper {
  position: relative;
  transition: transform 0.1s ease-out;
  transform-origin: center center;
  user-select: none;
  -webkit-user-drag: none;
  touch-action: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.viewer-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;
}

.viewer-toolbar {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 2rem;
  backdrop-filter: blur(10px);
  z-index: 10;
}

.zoom-level {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  min-width: 50px;
  text-align: center;
}

.viewer-toolbar :deep(.p-button) {
  color: white;
}

.viewer-toolbar :deep(.p-button:hover) {
  background: rgba(255, 255, 255, 0.1);
}
</style>
